import React, { useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import '../../../shared/StyledInput.css';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Matches LogActivityDrawer's own "Associated With" select — same shape
// react-select needs since it can't inherit styled-input's chrome via
// className.
const entitySelectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 30,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 14,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

// Reassigns which Commercial Entity an already-logged Activity belongs to —
// a deliberate, separate action from editing the Activity itself (see
// activityController.moveActivity's own comment), used to correct a
// mis-logged owner, e.g. a call logged against an Organisation that
// actually involved one specific Individual. Leaves a visible trace
// (moved_from_entity_id/moved_at/moved_by_user_id), surfaced back on the
// row by CommercialEntityDetailPage — this drawer itself only picks the
// destination and confirms.
function MoveActivityDrawer({ open, activity, onOpenChange, onMoved }) {
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadEntityOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) return callback([]);
    axios.get('/api/commercial-entities/quick-search', {
      params: { q: inputValue, excludeId: activity?.commercial_entity_id },
    })
      .then((res) => callback((res.data.data || []).map((r) => ({ value: String(r.id), label: r.name }))))
      .catch(() => callback([]));
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      setTarget(null);
      setError('');
    }
    onOpenChange(nextOpen);
  };

  const handleSave = async () => {
    if (!target) return;
    setSaving(true);
    setError('');
    try {
      const res = await axios.patch(`/api/activities/${activity.id}/move`, { commercial_entity_id: target.value });
      onMoved(res.data.data);
      handleClose(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Move failed');
    }
    setSaving(false);
  };

  return (
    <FilterDrawer
      open={open}
      onOpenChange={handleClose}
      title="Move Activity"
      description="Reassign which Organisation or Person this was actually logged against."
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={() => handleClose(false)} disabled={saving} />
          <SaveButton onClick={handleSave} loading={saving} disabled={!target} type="button" />
        </div>
      )}
    >
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Move to</label>
        <AsyncSelect
          cacheOptions
          defaultOptions={false}
          loadOptions={loadEntityOptions}
          value={target}
          onChange={setTarget}
          placeholder="Search people or companies..."
          styles={entitySelectStyles}
          noOptionsMessage={() => 'No matches found'}
          loadingMessage={() => 'Searching...'}
        />
      </div>
      <div style={{ fontSize: 12, color: '#9aa5b5' }}>
        A record of the original owner is kept, so this stays a visible correction rather than a silent edit.
      </div>
      {error && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 10 }}>{error}</div>}
    </FilterDrawer>
  );
}

export default MoveActivityDrawer;
